import { useState, useEffect, useRef } from "react";
import { Camera, RotateCcw, Loader2, Database, CheckCircle2, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import typeshieldWasher from "@/assets/typeshield-washer.jpg";
import typeshieldDishwasher from "@/assets/typeshield-dishwasher.jpg";
import typeshieldFridge from "@/assets/typeshield-fridge.jpg";
import { MatchingManualsPanel } from "@/components/MatchingManualsPanel";
import { ExplodedViewsPanel } from "@/components/workflow/ExplodedViewsPanel";

interface ExtractedData {
  hersteller?: string;
  modell?: string;
  typ?: string;
  baujahr?: number;
  alter_jahre?: number;
}

interface EprelVerification {
  verified: boolean;
  source: "eprel" | "ocr_only";
  eprelModelIdentifier?: string;
  eprelRegistrationNumber?: string;
  onMarketStartDate?: number[];
  verifiedBaujahr?: number;
}

interface RuleCondition {
  field: string;
  operator: string;
  value: any;
}

interface Rule {
  name: string;
  conditions: RuleCondition[];
}

type ScanResult = "eligible" | "not_eligible" | null;
type ProcessingStage = "ocr" | "eprel" | "rules" | null;

export default function DeviceScanner() {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState<ProcessingStage>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [eprelVerification, setEprelVerification] = useState<EprelVerification | null>(null);
  const [result, setResult] = useState<ScanResult>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setIsLoadingRules(true);
      const { data: uc } = await supabase
        .from("user_companies")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();

      const cId = uc?.company_id;
      setCompanyId(cId || null);

      if (cId) {
        const { data: cr } = await supabase
          .from("company_rules")
          .select("rules_json")
          .eq("company_id", cId);

        const allRules: Rule[] = [];
        (cr || []).forEach((row: any) => {
          if (Array.isArray(row.rules_json)) {
            allRules.push(...row.rules_json);
          }
        });
        setRules(allRules);
      }
      setIsLoadingRules(false);
    })();
  }, [user]);

  const evaluateCondition = (data: ExtractedData, condition: RuleCondition): boolean => {
    const fieldValue = (data as any)[condition.field];
    if (fieldValue === undefined || fieldValue === null) return false;

    switch (condition.operator) {
      case "equals":
        return String(fieldValue).toLowerCase() === String(condition.value).toLowerCase();
      case "not_equals":
        return String(fieldValue).toLowerCase() !== String(condition.value).toLowerCase();
      case "less_than":
        return Number(fieldValue) < Number(condition.value);
      case "greater_than":
        return Number(fieldValue) > Number(condition.value);
      case "in":
        if (Array.isArray(condition.value)) {
          return condition.value.some(
            (v: any) => String(v).toLowerCase() === String(fieldValue).toLowerCase()
          );
        }
        return false;
      case "not_in":
        if (Array.isArray(condition.value)) {
          return !condition.value.some(
            (v: any) => String(v).toLowerCase() === String(fieldValue).toLowerCase()
          );
        }
        return true;
      default:
        return false;
    }
  };

  const checkEligibility = (data: ExtractedData): ScanResult => {
    if (rules.length === 0) return "not_eligible";
    for (const rule of rules) {
      const allPass = rule.conditions.every((c) => evaluateCondition(data, c));
      if (allPass) return "eligible";
    }
    return "not_eligible";
  };

  const verifyWithEprel = async (data: ExtractedData): Promise<{ verifiedData: ExtractedData; verification: EprelVerification }> => {
    const currentYear = new Date().getFullYear();
    
    if (!data.modell && !data.hersteller) {
      return {
        verifiedData: data,
        verification: { verified: false, source: "ocr_only" },
      };
    }

    try {
      // Determine product group from type/model hints
      const typeHint = (data.typ || data.modell || "").toLowerCase();
      let productGroup = "washingmachines2019";
      if (typeHint.match(/g\s?\d|spül|geschirr|dish/i)) productGroup = "dishwashers2019";
      else if (typeHint.match(/kühl|freez|refrig|kgn|kgv|kge/i)) productGroup = "refrigeratingappliances2019";
      else if (typeHint.match(/trockn|dry|wt[a-z]/i)) productGroup = "tumbledryers";

      const searchTerm = data.modell || data.hersteller || "";

      const { data: eprelResult, error } = await supabase.functions.invoke("eprel-search", {
        body: { modelNumber: searchTerm, productGroup },
      });

      if (error || !eprelResult?.success) {
        console.log("EPREL search returned no results, using OCR data only");
        return {
          verifiedData: data,
          verification: { verified: false, source: "ocr_only" },
        };
      }

      const eprelData = eprelResult.data;
      const searchResults = eprelResult.searchResults;

      // Try to find a matching result
      const modelClean = (data.modell || "").toLowerCase().replace(/[\s\-]/g, "");
      let matchedEprel: any = null;

      if (searchResults && Array.isArray(searchResults)) {
        matchedEprel = searchResults.find((r: any) => {
          const eprelModel = (r.modelIdentifier || "").toLowerCase().replace(/[\s\-]/g, "");
          return eprelModel.includes(modelClean) || modelClean.includes(eprelModel.split(" ")[0]);
        });
      }

      if (!matchedEprel && eprelData?.modelIdentifier) {
        const eprelModel = eprelData.modelIdentifier.toLowerCase().replace(/[\s\-]/g, "");
        if (eprelModel.includes(modelClean) || modelClean.includes(eprelModel.split(" ")[0])) {
          matchedEprel = eprelData;
        }
      }

      if (matchedEprel) {
        // If we found a match, use EPREL's onMarketStartDate for baujahr verification
        let verifiedBaujahr = data.baujahr;
        const onMarket = matchedEprel.onMarketStartDate || eprelData?.onMarketStartDate;

        if (Array.isArray(onMarket) && onMarket.length >= 1) {
          verifiedBaujahr = onMarket[0]; // Year from EPREL
        }

        const verifiedAlter = verifiedBaujahr ? currentYear - verifiedBaujahr : data.alter_jahre;

        return {
          verifiedData: {
            ...data,
            hersteller: matchedEprel.supplierOrTrademark || eprelData?.supplierOrTrademark || data.hersteller,
            baujahr: verifiedBaujahr,
            alter_jahre: verifiedAlter,
          },
          verification: {
            verified: true,
            source: "eprel",
            eprelModelIdentifier: matchedEprel.modelIdentifier || eprelData?.modelIdentifier,
            eprelRegistrationNumber: matchedEprel.eprelRegistrationNumber || eprelData?.eprelRegistrationNumber,
            onMarketStartDate: onMarket,
            verifiedBaujahr,
          },
        };
      }

      return {
        verifiedData: data,
        verification: { verified: false, source: "ocr_only" },
      };
    } catch (err) {
      console.error("EPREL verification error:", err);
      return {
        verifiedData: data,
        verification: { verified: false, source: "ocr_only" },
      };
    }
  };

  const handleCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setExtractedData(null);
    setResult(null);
    setEprelVerification(null);
    setProcessingStage("ocr");

    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Step 1: OCR
      const { data, error } = await supabase.functions.invoke("device-check-ocr", {
        body: { imageBase64: base64 },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const extracted = data.extracted as ExtractedData;

      // Step 2: EPREL verification
      setProcessingStage("eprel");
      const { verifiedData, verification } = await verifyWithEprel(extracted);
      setExtractedData(verifiedData);
      setEprelVerification(verification);

      // Step 3: Rule evaluation
      setProcessingStage("rules");
      const scanResult = checkEligibility(verifiedData);
      setResult(scanResult);

      // Log to scan_logs
      if (user && companyId) {
        await supabase.from("scan_logs").insert({
          user_id: user.id,
          company_id: companyId,
          extracted_data: { ...verifiedData, _eprel: verification } as any,
          result: scanResult,
        });
      }
    } catch (err: any) {
      console.error("Scan error:", err);
      toast.error(err.message || "Scan fehlgeschlagen");
    } finally {
      setIsProcessing(false);
      setProcessingStage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleReset = () => {
    setExtractedData(null);
    setResult(null);
    setEprelVerification(null);
  };

  if (isLoadingRules) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Result screen
  if (result && extractedData) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 pb-24">
        <div className="w-full max-w-sm flex flex-col items-center text-center space-y-6">
          {result === "eligible" ? (
            <>
              <div className="w-28 h-28 rounded-full bg-accent/20 flex items-center justify-center">
                <span className="text-6xl">✅</span>
              </div>
              <h1 className="text-2xl font-bold text-accent-foreground">Gerät geeignet</h1>
            </>
          ) : (
            <>
              <div className="w-28 h-28 rounded-full bg-destructive/10 flex items-center justify-center">
                <span className="text-6xl">🛑</span>
              </div>
              <h1 className="text-2xl font-bold text-destructive">Gerät nicht geeignet</h1>
            </>
          )}

          <div className="w-full bg-card rounded-xl border border-border p-5 text-left space-y-2">
            <h2 className="font-semibold text-foreground mb-3">Erkannte Daten</h2>
            {[
              { label: "Hersteller", value: extractedData.hersteller },
              { label: "Modell", value: extractedData.modell },
              { label: "Typ", value: extractedData.typ },
              { label: "Baujahr", value: extractedData.baujahr },
              { label: "Alter (Jahre)", value: extractedData.alter_jahre },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium text-foreground">{value ?? "—"}</span>
              </div>
            ))}
          </div>

          {/* EPREL verification badge */}
          {eprelVerification && (
            <div className={`w-full rounded-xl border p-4 text-left text-sm ${
              eprelVerification.verified
                ? "bg-accent/10 border-accent/30"
                : "bg-muted border-border"
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {eprelVerification.verified ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    <span className="font-medium text-foreground">EPREL verifiziert</span>
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-muted-foreground">Nur OCR (kein EPREL-Treffer)</span>
                  </>
                )}
              </div>
              {eprelVerification.verified && (
                <div className="space-y-1 text-muted-foreground">
                  {eprelVerification.eprelModelIdentifier && (
                    <div className="flex justify-between">
                      <span>EPREL Modell</span>
                      <span className="font-medium text-foreground">{eprelVerification.eprelModelIdentifier}</span>
                    </div>
                  )}
                  {eprelVerification.eprelRegistrationNumber && (
                    <div className="flex justify-between">
                      <span>EPREL Nr.</span>
                      <span className="font-medium text-foreground">{eprelVerification.eprelRegistrationNumber}</span>
                    </div>
                  )}
                  {eprelVerification.verifiedBaujahr && (
                    <div className="flex justify-between">
                      <span>Marktstart (EPREL)</span>
                      <span className="font-medium text-foreground">{eprelVerification.verifiedBaujahr}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Repair Guide Center matches (model-first, brand fallback) */}
          {(extractedData.hersteller || extractedData.modell) && (
            <div className="w-full space-y-3 text-left">
              <ExplodedViewsPanel
                brand={extractedData.hersteller}
                applianceType={extractedData.typ}
                modelNumber={extractedData.modell}
                defaultOpen
              />
              <MatchingManualsPanel
                brand={extractedData.hersteller}
                modelNumber={extractedData.modell}
                defaultOpen
              />
            </div>
          )}

          <Button onClick={handleReset} className="w-full h-14 text-lg gap-2" size="lg">
            <RotateCcw className="w-5 h-5" />
            Scan again
          </Button>
        </div>
      </div>
    );
  }

  // Scanner screen
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 pb-24">
      <div className="w-full max-w-sm flex flex-col items-center text-center space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Pre Triage</h1>
          <p className="text-muted-foreground">
            Take picture of type shield to validate if appliance is suitable.
          </p>
          {rules.length === 0 && (
            <p className="text-sm text-destructive mt-2">
              ⚠️ No rules configured. Please contact an administrator.
            </p>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          className="hidden"
          id="camera-input"
        />

        <label
          htmlFor="camera-input"
          className={`w-40 h-40 rounded-full flex items-center justify-center cursor-pointer transition-all active:scale-95
            ${isProcessing
              ? "bg-muted pointer-events-none"
              : "bg-primary/10 hover:bg-primary/20 active:bg-primary/30"
            }`}
        >
          {isProcessing ? (
            processingStage === "eprel" ? (
              <Database className="w-16 h-16 text-primary animate-pulse" />
            ) : (
              <Loader2 className="w-16 h-16 text-primary animate-spin" />
            )
          ) : (
            <Camera className="w-16 h-16 text-primary" />
          )}
        </label>

        <p className="text-sm text-muted-foreground">
          {processingStage === "ocr"
            ? "Recognizing type shield..."
            : processingStage === "eprel"
            ? "Matching EPREL database..."
            : processingStage === "rules"
            ? "Checking rules..."
            : "Click to upload"}
        </p>

        <button
          type="button"
          onClick={() => setShowHelp(true)}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <HelpCircle className="w-4 h-4" />
          How does a type shield look like and where to find them
        </button>

        <Dialog open={showHelp} onOpenChange={setShowHelp}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>What is a type shield?</DialogTitle>
              <DialogDescription>
                A type shield (also called rating plate or nameplate) is a small label
                attached to every appliance. It contains the brand, model number, serial
                number and technical specifications needed to identify the device.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 mt-2 sm:grid-cols-3">
              <figure className="space-y-2">
                <img
                  src={typeshieldWasher}
                  alt="Washing machine type shield example"
                  loading="lazy"
                  width={768}
                  height={768}
                  className="w-full aspect-square object-cover rounded-lg border border-border"
                />
                <figcaption className="text-sm">
                  <span className="font-medium text-foreground">Washing machine</span>
                  <span className="block text-muted-foreground">
                    Inside the door frame, visible when the door is open.
                  </span>
                </figcaption>
              </figure>
              <figure className="space-y-2">
                <img
                  src={typeshieldDishwasher}
                  alt="Dishwasher type shield example"
                  loading="lazy"
                  width={768}
                  height={768}
                  className="w-full aspect-square object-cover rounded-lg border border-border"
                />
                <figcaption className="text-sm">
                  <span className="font-medium text-foreground">Dishwasher</span>
                  <span className="block text-muted-foreground">
                    On the side edge of the door, visible when opened.
                  </span>
                </figcaption>
              </figure>
              <figure className="space-y-2">
                <img
                  src={typeshieldFridge}
                  alt="Refrigerator type shield example"
                  loading="lazy"
                  width={768}
                  height={768}
                  className="w-full aspect-square object-cover rounded-lg border border-border"
                />
                <figcaption className="text-sm">
                  <span className="font-medium text-foreground">Refrigerator</span>
                  <span className="block text-muted-foreground">
                    On the inner wall of the fridge compartment.
                  </span>
                </figcaption>
              </figure>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Tip: make sure the label is clean, well-lit and the text is in focus before
              uploading the picture.
            </p>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
